import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { borrowerAPI, plaidAPI } from '../../services/api';
import { toast } from 'sonner';
import { usePlaidLink } from 'react-plaid-link';
import { 
  Shield, Upload, CheckCircle, Clock, AlertTriangle,
  DollarSign, Calendar, FileText, ArrowRight, Building2,
  RefreshCw, Link as LinkIcon
} from 'lucide-react';

const BorrowerVerification = () => {
  const [verification, setVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [plaidConfigured, setPlaidConfigured] = useState(false);
  const [linkToken, setLinkToken] = useState(null);
  const [plaidLoading, setPlaidLoading] = useState(false);
  const [formData, setFormData] = useState({
    statementDate: '',
    statementBalance: '',
    file: null
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchVerification();
    checkPlaidStatus();
  }, []);

  const fetchVerification = async () => {
    try {
      const response = await borrowerAPI.getVerification();
      setVerification(response.data);
    } catch (error) {
      // Verification might not exist yet
      if (error.response?.status === 404) {
        try {
          const response = await borrowerAPI.createVerification();
          setVerification(response.data);
        } catch (err) {
          console.error('Error creating verification:', err);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const checkPlaidStatus = async () => {
    try {
      const response = await plaidAPI.getStatus();
      setPlaidConfigured(response.data.configured);
    } catch (error) {
      console.error('Failed to check Plaid status');
    }
  };

  const fetchLinkToken = async () => {
    if (!plaidConfigured) return;
    setPlaidLoading(true);
    try {
      const response = await plaidAPI.createLinkToken();
      setLinkToken(response.data.link_token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize Plaid');
    } finally {
      setPlaidLoading(false);
    }
  };

  const onPlaidSuccess = useCallback(async (publicToken, metadata) => {
    setPlaidLoading(true);
    try {
      const response = await plaidAPI.exchangeToken(publicToken);
      toast.success(`Bank account connected! Verified liquidity: $${response.data.verified_liquidity?.toLocaleString()}`);
      fetchVerification();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect bank account');
    } finally {
      setPlaidLoading(false);
    }
  }, []);

  const { open: openPlaid, ready: plaidReady } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: (err) => {
      if (err) {
        console.error('Plaid error:', err);
      }
    }
  });

  const handlePlaidConnect = async () => {
    if (!linkToken) {
      await fetchLinkToken();
    }
    if (plaidReady) {
      openPlaid();
    } else {
      toast.info('Initializing secure connection...');
    }
  };

  useEffect(() => {
    if (linkToken && plaidReady && plaidLoading) {
      openPlaid();
      setPlaidLoading(false);
    }
  }, [linkToken, plaidReady, plaidLoading, openPlaid]);

  const refreshPlaidBalance = async () => {
    setPlaidLoading(true);
    try {
      const response = await plaidAPI.verifyBalance();
      toast.success(`Balance refreshed: $${response.data.verified_liquidity_amount?.toLocaleString()}`);
      fetchVerification();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh balance');
    } finally {
      setPlaidLoading(false);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setFormData({ ...formData, file: e.target.files[0] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.file || !formData.statementDate || !formData.statementBalance) {
      toast.error('Please fill in all fields');
      return;
    }

    setUploading(true);
    try {
      await borrowerAPI.uploadBankStatement(
        formData.statementDate,
        parseFloat(formData.statementBalance),
        formData.file
      );
      toast.success('Bank statement uploaded successfully!');
      fetchVerification();
      setFormData({ statementDate: '', statementBalance: '', file: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload bank statement');
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'approved') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full text-sm font-medium">
          <CheckCircle className="w-4 h-4" />
          Verified
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-full text-sm font-medium">
          <Clock className="w-4 h-4" />
          Pending Review
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400 rounded-full text-sm font-medium">
        <AlertTriangle className="w-4 h-4" />
        Not Started
      </span>
    );
  };

  if (loading) {
    return (
      <Layout title="Liquidity Verification">
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  const isVerified = verification?.verification_status === 'approved' || verification?.plaid_connected;

  return (
    <Layout title="Liquidity Verification">
      <div className="max-w-4xl mx-auto space-y-6 animate-slide-in-up">
        {/* Header Info */}
        <Card className="glass-effect border-blue-200 dark:border-blue-800 bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                  Verify Your Closing Liquidity
                </h2>
                <p className="text-slate-600 dark:text-slate-400">
                  To proceed with your deal, we need to verify that you have sufficient funds available 
                  for closing costs and project start. This helps ensure a smooth transaction process.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current Status */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-slate-900 dark:text-slate-100">Verification Status</CardTitle>
              {getStatusBadge(verification?.verification_status || 'not_started')}
            </div>
          </CardHeader>
          <CardContent>
            {verification?.verification_status === 'approved' ? (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="font-semibold text-green-700 dark:text-green-400">Verification Complete!</p>
                    <p className="text-sm text-green-600 dark:text-green-500">
                      Your liquidity of ${verification.statement_balance?.toLocaleString() || verification.plaid_verified_liquidity?.toLocaleString()} has been verified.
                    </p>
                  </div>
                </div>
              </div>
            ) : verification?.plaid_connected ? (
              <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="font-semibold text-green-700 dark:text-green-400">Bank Connected via Plaid!</p>
                    <p className="text-sm text-green-600 dark:text-green-500">
                      Verified liquidity: ${verification.plaid_verified_liquidity?.toLocaleString() || '0'}
                    </p>
                  </div>
                </div>
              </div>
            ) : verification?.statement_balance ? (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="font-semibold text-yellow-700 dark:text-yellow-400">Pending Review</p>
                    <p className="text-sm text-yellow-600 dark:text-yellow-500">
                      Your bank statement showing ${verification.statement_balance?.toLocaleString()} is under review.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-200 dark:border-slate-700">
                <p className="text-slate-600 dark:text-slate-400">
                  Please verify your liquidity using Plaid (instant) or by uploading a recent bank statement.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Plaid Section */}
        <Card className="glass-effect border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Instant Verification with Plaid
              {plaidConfigured && (
                <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full ml-2">
                  Recommended
                </span>
              )}
            </CardTitle>
            <CardDescription>
              Connect your bank account securely for instant balance verification
            </CardDescription>
          </CardHeader>
          <CardContent>
            {verification?.plaid_connected ? (
              <div className="space-y-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-semibold text-green-700 dark:text-green-400">Bank Connected</p>
                        <p className="text-sm text-green-600 dark:text-green-500">
                          Verified: ${verification.plaid_verified_liquidity?.toLocaleString() || '0'}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={refreshPlaidBalance}
                      disabled={plaidLoading}
                      className="border-green-400 text-green-700 hover:bg-green-100"
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${plaidLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </div>
                </div>
                {verification.plaid_accounts && verification.plaid_accounts.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Connected Accounts:</p>
                    {verification.plaid_accounts.map((acc, idx) => (
                      <div key={idx} className="flex justify-between p-2 bg-slate-50 dark:bg-slate-800/50 rounded">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {acc.name} ({acc.type}) •••{acc.mask}
                        </span>
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          ${acc.balance?.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : plaidConfigured ? (
              <Button 
                onClick={handlePlaidConnect}
                disabled={plaidLoading}
                className="w-full gradient-primary border-0"
                data-testid="connect-plaid-btn"
              >
                {plaidLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Connecting...
                  </>
                ) : (
                  <>
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Connect Bank Account with Plaid
                  </>
                )}
              </Button>
            ) : (
              <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Plaid integration is being configured. Please use the bank statement upload option below.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Form - Show only if not verified via Plaid */}
        {!verification?.plaid_connected && verification?.verification_status !== 'approved' && (
          <Card className="glass-effect border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload Bank Statement
              </CardTitle>
              <CardDescription>
                Upload a recent bank statement (within 30 days) showing your available balance for closing costs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="statementDate">Statement Date</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        id="statementDate"
                        type="date"
                        value={formData.statementDate}
                        onChange={(e) => setFormData({ ...formData, statementDate: e.target.value })}
                        required
                        className="pl-10"
                        data-testid="statement-date"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="statementBalance">Available Balance ($)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        id="statementBalance"
                        type="number"
                        placeholder="Enter available balance"
                        value={formData.statementBalance}
                        onChange={(e) => setFormData({ ...formData, statementBalance: e.target.value })}
                        required
                        min="0"
                        className="pl-10"
                        data-testid="statement-balance"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="file">Bank Statement (PDF or Image)</Label>
                  <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                    <input
                      id="file"
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="statement-file"
                    />
                    <label htmlFor="file" className="cursor-pointer">
                      <FileText className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                      {formData.file ? (
                        <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                          {formData.file.name}
                        </p>
                      ) : (
                        <p className="text-sm text-slate-600 dark:text-slate-400">
                          Click to upload or drag and drop
                        </p>
                      )}
                    </label>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full gradient-primary border-0"
                  disabled={uploading}
                  data-testid="upload-statement-btn"
                >
                  {uploading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Bank Statement
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Continue Button */}
        <div className="flex justify-end">
          <Button 
            onClick={() => navigate('/borrower')}
            variant="outline"
            className="gap-2"
          >
            Back to Dashboard
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </Layout>
  );
};

export default BorrowerVerification;
